/**
 * My Requests — list page showing the current user's submitted requests.
 *
 * Top of the page is the catalog tile strip — click a tile to start a new
 * request of that type. Below is the table of past + in-flight submissions.
 */

import { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Inbox } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'

// ─────────────────── Types ───────────────────

type RequestType = {
  id: number
  code: string
  name: string
  description: string
  icon: string
  is_active: boolean
  display_order: number
}

type RequestListRow = {
  id: number
  code: string
  request_type: number
  request_type_code: string
  request_type_name: string
  request_type_icon: string
  requester: number
  requester_name: string
  summary: string
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'FULFILLED'
  current_step: number | null
  current_step_name: string | null
  current_approver: number | null
  current_approver_name: string | null
  submitted_at: string | null
  resolved_at: string | null
  created_at: string
}

const STATUS_COLOR: Record<RequestListRow['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-600',
  FULFILLED: 'bg-green-100 text-green-800',
}

const STATUS_LABEL: Record<RequestListRow['status'], string> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  FULFILLED: 'Fulfilled',
}

// ─────────────────── Page ───────────────────

export default function RequestsMyList() {
  const navigate = useNavigate()

  const typesQ = useQuery({
    queryKey: ['requests', 'types'],
    queryFn: async () => (await api.get<RequestType[]>('/request-types/')).data,
  })
  const myQ = useQuery({
    queryKey: ['requests', 'mine'],
    queryFn: async () =>
      (await api.get<RequestListRow[]>('/requests/?mine=true')).data,
  })
  const countsQ = useQuery({
    queryKey: ['requests', 'inbox-counts'],
    queryFn: async () =>
      (await api.get<{ mine: number; awaiting_me: number; to_fulfill: number }>(
        '/requests/inbox-counts/',
      )).data,
  })

  const types = (typesQ.data || []).filter((t) => t.is_active)
  const rows = myQ.data || []
  const counts = countsQ.data

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground tracking-tight">My Requests</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Submit a new request or check the status of your in-flight ones.
          </p>
        </div>
        {counts && (counts.awaiting_me > 0 || counts.to_fulfill > 0) && (
          <Link to="/admin/requests/inbox" className="shrink-0">
            <Button variant="outline">
              <Inbox className="h-3 w-3 mr-1" />
              {counts.awaiting_me + counts.to_fulfill} awaiting you
            </Button>
          </Link>
        )}
      </div>

      {/* Catalog tiles */}
      {types.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Submit a new request
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {types.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => navigate(`/admin/requests/new?type=${t.code}`)}
                className="bg-card rounded-lg shadow p-4 text-left hover:shadow-md transition border border-border"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl shrink-0 select-none">{t.icon || '📝'}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {t.description}
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        Your submissions
      </h3>
      {myQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          You haven't submitted any requests yet.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Type</Th>
                <Th>Summary</Th>
                <Th>Status</Th>
                <Th>Current step</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/requests/${r.id}`)}
                >
                  <Td className="font-mono text-xs">{r.code}</Td>
                  <Td className="text-xs">
                    <span className="mr-1">{r.request_type_icon || '📝'}</span>
                    {r.request_type_name}
                  </Td>
                  <Td className="text-sm font-medium max-w-[28rem] truncate">{r.summary}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {r.current_step_name || '—'}
                    {r.current_approver_name && (
                      <span className="block text-[10px]">({r.current_approver_name})</span>
                    )}
                  </Td>
                  <Td className="text-xs text-muted-foreground whitespace-nowrap">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

// ─────────────────── Helpers ───────────────────

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
